import {ReactElement, useState} from "react";
import {Field, Label, Toggle} from "@zendeskgarden/react-forms";
import {Body, Footer, FooterItem, Header} from "@zendeskgarden/react-modals";
import {Button} from "@zendeskgarden/react-buttons";
import {ThemeProvider} from "styled-components";
import {ViziblyTheme} from "../../../../../styles/zendesk-garden/ViziblyZDGTheme";
import {Inline} from "@zendeskgarden/react-loaders";
import {COLORS} from "../../../../../constants/Colors";
import {Separator} from "@zendeskgarden/react-dropdowns";
import TemplatePropertyValidator, {TFormError} from "../../logic/templatePropertyValidator";
import TemplatePropertyFormInput from "./TemplatePropertyFormInput";

import * as css from "./styles/templatePropertyFormStyles.module.scss";
import TemplatePropertyFormDropdown from "./TemplatePropertyFormDropdown";

export type TTemplatePropertyRecord = {
    id?: string;
    name: string;
    isDeactivated: boolean;
    reforecastStartMonthIndex: number;
    reforecastYear?: number;
}

export interface ITemplatePropertyForm {
    isCreate?: boolean,
    templateProperty: TTemplatePropertyRecord,
    loading: boolean,
    onClose: () => void,
    onChange: (updates: Partial<TTemplatePropertyRecord>) => void,
    onSubmit: () => void,
}

export default function TemplatePropertyForm(props: ITemplatePropertyForm): ReactElement {
    const {
        isCreate = false,
        templateProperty,
        loading,
        onClose,
        onChange,
        onSubmit,
    } = props;

    const [formErrors, setFormErrors] = useState<TFormError[]>([]);
    const templatePropertyValidator = new TemplatePropertyValidator();

    // Months for the dropdown
    const months = [
        { value: 0, name: "January" },
        { value: 1, name: "February" },
        { value: 2, name: "March" },
        { value: 3, name: "April" },
        { value: 4, name: "May" },
        { value: 5, name: "June" },
        { value: 6, name: "July" },
        { value: 7, name: "August" },
        { value: 8, name: "September" },
        { value: 9, name: "October" },
        { value: 10, name: "November" },
        { value: 11, name: "December" }
    ];

    function checkValidOnBlur(field: string): void {
        if (formErrors.find(x => x.field == field)) {
            setFormErrors(templatePropertyValidator.validate(templateProperty));
        }
    }

    function validate(): boolean {
        const errors = templatePropertyValidator.validate(templateProperty);
        setFormErrors(errors);
        return errors.length === 0;
    }

    function submit(): void {
        if (validate()) {
            onSubmit();
        }
    }

    return (
        <ThemeProvider theme={ViziblyTheme}>
            <Header>{isCreate ? "Create" : "Edit"} Template Property</Header>
            <Body>
                <form className={css.templatePropertyForm}>
                    <TemplatePropertyFormInput
                        label="Name"
                        placeholder="Enter name"
                        required
                        value={templateProperty.name}
                        error={formErrors.find(e => e.field === "name")?.message}
                        onChange={(e) => onChange({ name: e.target.value })}
                        onBlur={() => checkValidOnBlur('name')}
                    />

                    <TemplatePropertyFormDropdown
                        label="Reforecast Start Month"
                        required
                        downshiftProps={{itemToString: (item: typeof months[0] | undefined) => item ? (item.name || item.value.toString()) : ""}}
                        selection={months[templateProperty.reforecastStartMonthIndex]}
                        options={months}
                        error={formErrors.find(e => e.field === "reforecastStartMonthIndex")?.message}
                        onSelect={(item) => onChange({reforecastStartMonthIndex: item.value as number})}
                        onBlur={() => checkValidOnBlur('reforecastStartMonthIndex')}
                    />

                    <Field className={css.fieldMargin}>
                        <Toggle
                            checked={templateProperty.isDeactivated}
                            onChange={(e) => onChange({ isDeactivated: e.target.checked })}
                        >
                            <Label>Deactivated</Label>
                        </Toggle>
                    </Field>
                </form>
            </Body>
            <Separator />
            <Footer>
                <FooterItem>
                    <Button onClick={onClose} isBasic disabled={loading}>
                        Cancel
                    </Button>
                </FooterItem>
                <FooterItem>
                    <Button isPrimary onClick={submit} disabled={loading}>
                        {loading ? (
                            <Inline size={24} color={COLORS.POSITIVE_COLOR} aria-label="loading" />
                        ) : (
                            <span>Save</span>
                        )}
                    </Button>
                </FooterItem>
            </Footer>
        </ThemeProvider>
    );
}
